/**
 * API Client
 * Cliente HTTP para comunicação com backend
 * Trata autenticação, erros, e retry automático
 */

import { API_CONFIG } from '@/constants/api';
import { getAuthToken, removeAuthToken } from '@/utils/auth';

export interface ApiRequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  token?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  /** Código de domínio do backend quando disponível (ex.: MEMED_DISABLED). */
  code?: string;
  status?: number;
  /** Campos faltantes (ex.: perfil Memed incompleto). */
  missingFields?: string[];
}

class ApiClient {
  private baseUrl: string;
  private timeout: number;
  private retryAttempts: number;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.retryAttempts = API_CONFIG.RETRY_ATTEMPTS;
  }

  /**
   * Get token from localStorage
   */
  private getToken(): string | null {
    return getAuthToken();
  }

  /**
   * Build headers with authentication
   */
  private buildHeaders(
    config?: ApiRequestConfig,
    endpoint?: string,
  ): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...config?.headers,
    };

    // Get token from config or utils
    const token = config?.token || this.getToken();
    
    // Explicitly check for valid token strings
    if (token && token.length > 10) {
      headers['Authorization'] = `Bearer ${token}`;
      // Log only in development or with a flag to avoid leaking tokens in production console
      if (import.meta.env.DEV) {
        console.log(`[API] 🔐 Authorization header added (token length: ${token.length})`);
      }
    } else {
      const isPublicAuthRoute =
        !!endpoint &&
        (endpoint.includes('/auth/login') ||
          endpoint.includes('/auth/register') ||
          endpoint.includes('/auth/refresh'));

      if (!isPublicAuthRoute) {
        console.warn(`[API] ⚠️ No valid token found for request. URL: ${endpoint || config?.method || 'GET'}`);
      }
    }

    return headers;
  }

  /**
   * Make API request with retry logic
   */
  async request<T = any>(
    endpoint: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= this.retryAttempts; attempt++) {
      try {
        const response = await this.makeRequest<T>(endpoint, config);
        return response;
      } catch (error) {
        lastError = error as Error;
        
        // Intercept 401/403 errors for session management
        if (error instanceof ApiError) {
          const status = Number(error.status);
          
          // 401 Unauthorized - Token expired or invalid
          if (status === 401) {
            console.error(`[API] 🔐 Unauthorized (401) at ${endpoint}.`);
            
            if (typeof window !== 'undefined') {
              const currentPath = window.location.pathname;
              const isLoginPage = currentPath.includes('/login');
              const isRegisterPage = currentPath.includes('/register');
              const hadAuthHeader = error.authSent === true;
              
              // Só limpa sessão se o request realmente enviou Bearer e foi rejeitado
              if (!isLoginPage && !isRegisterPage && hadAuthHeader) {
                console.warn('[API] Session invalid. Clearing data and redirecting to login.');
                this.clearSession();
                window.location.href = `/login?error=session_expired&redirect=${encodeURIComponent(currentPath)}`;
              }
            }
            
            return {
              success: false,
              error: 'Sessão expirada ou acesso não autorizado.',
              message: 'Sessão expirada',
              status: 401,
              code:
                error.response && typeof error.response === 'object'
                  ? (error.response as { code?: string }).code
                  : undefined,
            };
          }

          // 403 Forbidden - Role mismatch vs conectividade
          if (status === 403) {
            const isAdminEndpoint = endpoint.includes('/admin');
            if (isAdminEndpoint) {
              console.warn(
                `[API] FORBIDDEN esperado por role incorreta (403) em endpoint admin: ${endpoint}. Não é erro de conectividade.`,
              );
            } else {
              console.error(`[API] 🚫 Forbidden (403) at ${endpoint}. Access denied.`);
            }
            return {
              success: false,
              error: 'Você não tem permissão para acessar este recurso.',
              message: 'Acesso negado',
              status: 403,
              code:
                error.response && typeof error.response === 'object'
                  ? (error.response as { code?: string }).code
                  : undefined,
            };
          }

          // Other client errors
          if (status >= 400 && status < 500) {
            console.error(`[API] Client error (${status}) at ${endpoint}:`, error.message);
            // Don't retry on client errors
            break;
          }

          // Memed fora do ar / kill-switch: não faz sentido retryar 4x
          const errorCode =
            error.response && typeof error.response === 'object'
              ? (error.response as { code?: string }).code
              : undefined;
          if (
            status === 503 &&
            (errorCode === 'MEMED_UNAVAILABLE' || errorCode === 'MEMED_DISABLED')
          ) {
            console.warn(
              `[API] Memed indisponível (${errorCode}) em ${endpoint}. Sem retry.`,
            );
            break;
          }
        }

        console.error(`[API] Request failed (attempt ${attempt + 1}):`, error);

        // Wait before retrying (exponential backoff)
        if (attempt < this.retryAttempts) {
          await new Promise((resolve) =>
            setTimeout(resolve, Math.pow(2, attempt) * 1000),
          );
        }
      }
    }

    if (lastError instanceof ApiError) {
      const responsePayload =
        lastError.response && typeof lastError.response === 'object'
          ? (lastError.response as { code?: string; missingFields?: string[] })
          : undefined;
      return {
        success: false,
        error: lastError.message || 'Erro na comunicação com o servidor',
        message: lastError.message || 'Request failed after retries',
        status: Number(lastError.status) || undefined,
        code: responsePayload?.code,
        missingFields: Array.isArray(responsePayload?.missingFields)
          ? responsePayload.missingFields
          : undefined,
      };
    }

    return {
      success: false,
      error: lastError?.message || 'Erro na comunicação com o servidor',
      message: lastError?.message || 'Request failed after retries',
    };
  }

  /**
   * Helper to clear all auth data
   */
  private clearSession() {
    removeAuthToken();
  }

  /**
   * Make single request
   */
  private async makeRequest<T = any>(
    endpoint: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.buildHeaders(config, endpoint);
    const authSent = !!headers['Authorization'];

    const fetchConfig: RequestInit = {
      method: config?.method || 'GET',
      headers,
      ...(config?.body && { body: JSON.stringify(config.body) }),
    };

    console.log(`[API] 🚀 ${fetchConfig.method} ${url}`, {
      hasToken: authSent,
      tokenPreview: headers['Authorization'] ? `${headers['Authorization'].substring(0, 15)}...` : 'NONE',
    });

    // Create abort controller for timeout
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...fetchConfig,
        signal: abortController.signal,
      });

      let data: any = {};
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || response.statusText };
      }

      console.log(`[API] Response status: ${response.status}`, data);

      const extractErrorMessage = (payload: any): string => {
        if (!payload || typeof payload !== 'object') {
          return 'Request failed';
        }
        if (typeof payload.error === 'string' && payload.error !== 'Bad Request') {
          return payload.error;
        }
        if (typeof payload.message === 'string') {
          return payload.message;
        }
        if (Array.isArray(payload.message)) {
          return payload.message.join(', ');
        }
        if (payload.message && typeof payload.message === 'object') {
          if (typeof payload.message.error === 'string') return payload.message.error;
          if (typeof payload.message.message === 'string') return payload.message.message;
        }
        if (typeof payload.error === 'string') return payload.error;
        return 'Request failed';
      };

      // Se a resposta retornar success: false no body, tratar como erro também
      // (inclui caso legado 201 + success:false — nunca retry em erro de domínio)
      if (data.success === false) {
        const httpStatus = Number(response.status);
        const normalizedStatus =
          httpStatus >= 200 && httpStatus < 300 ? 400 : httpStatus;
        console.error(`[API] Backend returned success:false:`, {
          status: httpStatus,
          normalizedStatus,
          error: data.error,
          message: data.message,
          url,
          method: fetchConfig.method,
        });
        throw new ApiError(
          extractErrorMessage(data),
          normalizedStatus,
          data,
          authSent,
        );
      }

      if (!response.ok) {
        console.error(`[API] Error response:`, {
          status: response.status,
          error: data.error,
          message: data.message,
          url,
          method: fetchConfig.method,
        });
        throw new ApiError(
          extractErrorMessage(data),
          Number(response.status),
          data,
          authSent,
        );
      }

      console.log(`[API] Success response:`, {
        status: response.status,
        hasData: !!data,
      });
      return {
        success: true,
        data: data.data || data,
      };
    } catch (error) {
      console.error(`[API] Fetch error:`, {
        error,
        message: error instanceof Error ? error.message : 'Unknown error',
        url,
        method: fetchConfig.method,
      });
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * GET request
   */
  async get<T = any>(
    endpoint: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T = any>(
    endpoint: string,
    body?: any,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  /**
   * PUT request
   */
  async put<T = any>(
    endpoint: string,
    body?: any,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    endpoint: string,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    endpoint: string,
    body?: any,
    config?: ApiRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body });
  }
}

/**
 * Custom error for API requests
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response: any,
    public authSent: boolean = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
